import { useEffect, useState } from "react";
import { MessageCircle } from "lucide-react";
import { ApiError, post } from "../api/client";
import { nowIn } from "../lib/date";
import { Logo } from "./Logo";
import "./LoginPage.css";

const ERRORS: Record<string, string> = {
  unknown_number: "Não encontramos esse número. Mande um “oi” para o Macro no WhatsApp primeiro e tente de novo.",
  too_many: "Muitos códigos pedidos em pouco tempo. Espere alguns minutos.",
  send_failed: "Não consegui enviar a mensagem agora. Tente de novo em instantes.",
  invalid_code: "Código incorreto ou expirado.",
  invalid_link: "Esse link expirou ou já foi usado. Peça outro ao Macro (“link da agenda”) ou entre com seu número."
};

/** Passwordless login: magic link from the bot, or a code sent over WhatsApp. */
export function LoginPage({ onLoggedIn }: { onLoggedIn: () => void }) {
  const [step, setStep] = useState<"phone" | "code" | "magic">(() => (new URLSearchParams(window.location.search).get("t") ? "magic" : "phone"));
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (step !== "magic") return;
    const token = new URLSearchParams(window.location.search).get("t")!;
    post("/api/v1/auth/magic", { token })
      .then(() => {
        window.history.replaceState(null, "", "/");
        onLoggedIn();
      })
      .catch((e) => {
        window.history.replaceState(null, "", "/entrar");
        setError(ERRORS[(e as ApiError).code] ?? ERRORS.invalid_link);
        setStep("phone");
      });
  }, [step, onLoggedIn]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(ERRORS[(e as ApiError).code] ?? (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login">
      <div className="login-card">
        <Logo today={nowIn(Intl.DateTimeFormat().resolvedOptions().timeZone).date} />
        {step === "magic" ? (
          <div className="center-fill" style={{ height: 120 }}>
            <div className="spinner" />
          </div>
        ) : step === "phone" ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => {
                await post("/api/v1/auth/request-code", { phone });
                setStep("code");
              });
            }}
          >
            <h1>Entrar</h1>
            <p className="login-sub">Use o número de WhatsApp em que você conversa com o Macro. Vamos te mandar um código por lá.</p>
            <input
              className="field filled"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              autoFocus
              placeholder="(11) 98888-7777"
              aria-label="Número de WhatsApp"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            {error && <p className="login-error" role="alert">{error}</p>}
            <button className="btn primary login-btn" disabled={busy || phone.replace(/\D/g, "").length < 10}>
              <MessageCircle size={18} /> Enviar código pelo WhatsApp
            </button>
            <p className="login-hint">Dica: no WhatsApp, peça ao Macro “link da agenda” para entrar direto.</p>
          </form>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => {
                await post("/api/v1/auth/verify", { phone, code });
                onLoggedIn();
              });
            }}
          >
            <h1>Digite o código</h1>
            <p className="login-sub">Enviamos um código de 6 dígitos para o seu WhatsApp.</p>
            <input
              className="field filled login-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
              placeholder="000000"
              aria-label="Código"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />
            {error && <p className="login-error" role="alert">{error}</p>}
            <button className="btn primary login-btn" disabled={busy || code.length !== 6}>
              Entrar
            </button>
            <button type="button" className="btn text" onClick={() => { setStep("phone"); setCode(""); setError(null); }}>
              Usar outro número
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
